"use client";

/**
 * Tristate hook that decides whether the Inbox tab should appear
 * in the sequence detail tab strip.
 *
 * Phase L · Task 60 of plan-06 (spec §4.6).
 *
 * - Returns `true` once `/inbox/summary` confirms the sequence
 *   sender has at least one connected mailbox OR Social account.
 * - Returns `false` when both flags are `false` (the sender pool
 *   has nothing to deliver replies through, so the inbox surface
 *   would always be empty).
 * - Returns `undefined` while the summary query is in flight,
 *   which the caller treats as "render the tab to avoid a
 *   flash-on / flash-off when a recruiter lands directly on
 *   `?tab=inbox`".
 *
 * This hook intentionally lives in `features/inbox` (not
 * `features/sequences`) so the inbox feature owns the contract:
 * if spec 06 ever moves the visibility signal off the
 * `/inbox/summary` payload, only this file changes.
 */

import { useGetInboxSummary } from "@/lib/api/generated";

export type ShowInboxTab = boolean | undefined;

export function useShowInboxTab(sequenceId: string): ShowInboxTab {
  const summaryQuery = useGetInboxSummary(sequenceId, {
    query: { enabled: Boolean(sequenceId) },
  });

  if (summaryQuery.isLoading || !summaryQuery.data) {
    return undefined;
  }

  const summary = summaryQuery.data;
  return Boolean(summary.has_email) || Boolean(summary.has_linkedin);
}
